/** @jsxImportSource @emotion/react */
"use client";

import type { Interpolation } from "@emotion/react";
import { I18nMessage, useTranslations } from "@i18n/react";
import { HallowAnalyticsEvent, useAnalytics } from "@packages/analytics";
import { useRequestMe } from "@packages/sdk";
import type { TextProps, ThemeType } from "@packages/ui";
import {
  Button,
  CloseIcon,
  createModal,
  Form,
  FormField,
  IconButton,
  standardSchemaResolver,
  Text,
  useForm,
  useModal,
  useValidator,
} from "@packages/ui";
import * as RadixUIDialog from "@radix-ui/react-dialog";
import { useCallback, useRef } from "react";
import { useMediaQuery } from "usehooks-ts";

import { useModalResolution } from "../hooks/useModalResolution";
import type { UseSwitchProfilesProps } from "../hooks/useSwitchProfiles";
import { useSwitchProfiles } from "../hooks/useSwitchProfiles";
import { ScreenNames } from "../lib/enums/screenNames";
import { Screens } from "../lib/enums/screens";
import type { PinSchema } from "../lib/schemas/pinSchema";
import { pinSchema } from "../lib/schemas/pinSchema";
import { useContext } from "../Provider";
import { useRequestMePinForgotPost } from "../requests/useRequestMePinForgotPost";
import { useRequestMePinVerifyPost } from "../requests/useRequestMePinVerifyPost";
import { CardModal } from "./blocks/CardModal";
import { PinInput } from "./blocks/PinInput";

export enum EnterPinType {
  SwitchProfile = "switchProfile",
  VerifyPin = "verifyPin",
}

const ENTER_PIN_DESCRIPTION_MESSAGES = {
  [EnterPinType.SwitchProfile]: "general_words_to_switch_profiles",
  [EnterPinType.VerifyPin]: "enter_parent_pin_to_continue_subtitle",
} as const;

type EnterPinProps =
  | (UseSwitchProfilesProps & {
      onDismiss?: () => void;
      type: EnterPinType.SwitchProfile;
      userId: number;
    })
  | {
      onDismiss?: () => void;
      onSuccess?: (pin: string) => void;
      type: EnterPinType.VerifyPin;
    };

const cssDivButtonsContainer: Interpolation<ThemeType> = (theme) => ({
  display: "flex",
  flexDirection: "column",
  marginTop: theme.spacing.m,
  width: theme.spacing.full,
  [`@media ${Screens.Large}`]: {
    marginTop: theme.spacing.xxl,
  },
});
const cssDivHeaderContainer: Interpolation<ThemeType> = (theme) => ({
  alignItems: "center",
  display: "flex",
  justifyContent: "flex-end",
  width: theme.spacing.full,
});
const cssDivTextContainer: Interpolation<ThemeType> = (theme) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing.ms,
  width: theme.spacing.full,
});
const cssLabel: Interpolation<ThemeType> = (theme) => ({
  alignItems: "center",
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing.l,
  justifyContent: "center",
});
const cssModalContent: Interpolation<ThemeType> = (theme) => ({
  gap: theme.spacing.s,
});
const cssTextTitle: Interpolation = {
  margin: 0,
  textAlign: "center",
};
const cssTextDescription: Interpolation<ThemeType> = (theme) => ({
  color: theme.semanticColors.neutralsHigher,
  margin: 0,
  textAlign: "center",
});

export const EnterPin = createModal(
  ({ onDismiss, onSuccess, type, ...props }: EnterPinProps) => {
    const modal = useModal();

    const t = useTranslations();
    const { track } = useAnalytics();
    const { setToast } = useContext();
    const isLarge = useMediaQuery(Screens.Large);
    const {
      query: { data: me },
    } = useRequestMe();
    const mutationPostMePinForgot = useRequestMePinForgotPost();
    const { error, switchProfiles } = useSwitchProfiles({
      onSuccess: onSuccess as UseSwitchProfilesProps["onSuccess"],
    });
    const verifyMutation = useRequestMePinVerifyPost();
    const resolvedWithTrueRef = useRef(false);

    const { resolver } = useValidator<PinSchema>(
      standardSchemaResolver(pinSchema),
    );
    const form = useForm<PinSchema>({
      defaultValues: { pin: "" },
      resolver,
    });

    const onClose = useCallback(() => {
      if (type === EnterPinType.VerifyPin && !resolvedWithTrueRef.current) {
        onDismiss?.();
      }
      form.reset();
    }, [form.reset, onDismiss, type]);
    const { onOpenChange, resolve } = useModalResolution({ modal, onClose });

    const onClickForgotPin = useCallback(async () => {
      track({
        event: HallowAnalyticsEvent.TappedForgotParentPin,
        properties: { screen_name: ScreenNames.ParentPinInput },
      });
      const pathname =
        typeof window !== "undefined" ? window.location.pathname : undefined;
      const redirectPath =
        type === EnterPinType.SwitchProfile && pathname
          ? `${pathname}?open_switch_profiles=true`
          : pathname;
      try {
        const response = await mutationPostMePinForgot.mutateAsync(
          redirectPath ? { body: { redirect_path: redirectPath } } : {},
        );
        if (response.success_text) setToast(response.success_text);
      } catch (error) {
        setToast(error.message, "error");
      }
    }, [mutationPostMePinForgot.mutateAsync, setToast, track, type]);
    const onSubmit = useCallback(
      async (data: PinSchema) => {
        if (type === EnterPinType.SwitchProfile) {
          try {
            await switchProfiles({ pin: data.pin, userId: props["userId"] });

            resolvedWithTrueRef.current = true;
            resolve(true, () => {
              onClose();
              modal.hide();
            });
          } catch {
            track({
              event: HallowAnalyticsEvent.EnteredIncorrectParentPIN,
              properties: {
                error: "incorrect_pin",
                screen_name: ScreenNames.ParentPinInput,
              },
            });
          }
        }

        if (type === EnterPinType.VerifyPin) {
          try {
            await verifyMutation.mutateAsync({
              body: { pin: data.pin },
            });
            resolvedWithTrueRef.current = true;
            resolve(true, () => {
              onSuccess?.(data.pin);
              onClose();
              modal.remove();
            });
          } catch {
            track({
              event: HallowAnalyticsEvent.EnteredIncorrectParentPIN,
              properties: {
                error: "incorrect_pin",
                screen_name: ScreenNames.ParentPinInput,
              },
            });
            form.setError("pin", {
              message: t("profile_switcher_incorrect_pin_error"),
            });
          }
        }
      },
      [
        form,
        modal.hide,
        onClose,
        onSuccess,
        props,
        resolve,
        switchProfiles,
        t,
        track,
        type,
        verifyMutation,
      ],
    );

    return (
      <CardModal
        contentCss={cssModalContent}
        isOpen={modal.visible}
        onOpenAutoFocus={(event) => {
          event.preventDefault();
          document.getElementById("pin-0")?.focus();
        }}
        onOpenChange={onOpenChange}
      >
        <div css={cssDivHeaderContainer}>
          <RadixUIDialog.Close asChild>
            <IconButton
              icon={<CloseIcon size={20} />}
              size="s"
              variant={isLarge ? "transparentPrimary" : "neutral"}
            />
          </RadixUIDialog.Close>
        </div>
        <Form formProps={{ onSubmit: form.handleSubmit(onSubmit) }} {...form}>
          <label css={cssLabel}>
            <div css={cssDivTextContainer}>
              <RadixUIDialog.Title asChild>
                <Text
                  css={cssTextTitle}
                  {...({
                    size: isLarge ? "m" : "xl",
                    type: isLarge ? "headline" : "title",
                  } as TextProps)}
                >
                  <I18nMessage message="general_words_enter_parent_pin" />
                </Text>
              </RadixUIDialog.Title>
              <RadixUIDialog.Description asChild>
                <Text css={cssTextDescription} size="m" type="body">
                  <I18nMessage message="general_words_to_switch_profiles" />
                </Text>
              </RadixUIDialog.Description>
            </div>
            <FormField
              control={form.control}
              name="pin"
              render={({ field, fieldState }) => (
                <PinInput
                  {...field}
                  autoSubmit
                  error={
                    type === EnterPinType.VerifyPin
                      ? fieldState.error?.message
                      : error?.message
                  }
                  isCentered
                  onValueChange={field.onChange}
                />
              )}
              showErrorMessage={false}
            />
          </label>
        </Form>
        <div css={cssDivButtonsContainer}>
          {(type === EnterPinType.SwitchProfile || !me?.is_kid) && (
            <Button
              isFullWidth
              onClick={onClickForgotPin}
              size={isLarge ? "l" : "m"}
              type="button"
              variant={isLarge ? "transparentPrimary" : "transparentNeutral"}
            >
              {isLarge ? (
                <I18nMessage message="kids_profile_forgot_pin" />
              ) : (
                <Text size="m" type="body">
                  <I18nMessage message="kids_profile_forgot_pin" />
                </Text>
              )}
            </Button>
          )}
        </div>
      </CardModal>
    );
  },
);
